import dayjs from 'dayjs';
import { getIconUrl, imageEntities } from '../../../../utils/ImagesCreator';
import { getReadableTextColor } from './getReadableTextColor';
import { isWorkerFullyClosed, getWorkerClosuresForDate, parseClosureInterval } from './closureHelpers';
import { isCalendarBreak } from './taskDisplay';

const getServiceDurationMinutes = (service, servicesCatalog, bookingDurationMinutes) => {
  if (service?.durata != null) return Number(service.durata);

  const serviceId = service?.idServizio || service?.idservezio;
  const fromCatalog = servicesCatalog?.find(
    (catalogService) =>
      catalogService.idServizio === serviceId || catalogService.idservezio === serviceId,
  );

  if (fromCatalog?.durata != null) return Number(fromCatalog.durata);
  return bookingDurationMinutes;
};

const getMinServiceDurationInGroup = (group, servicesCatalog) => {
  const durations = [];

  group.forEach((task) => {
    const bookingDurationMinutes = task.unixSize / 60;

    if (task.servizi?.length) {
      task.servizi.forEach((service) => {
        durations.push(getServiceDurationMinutes(service, servicesCatalog, bookingDurationMinutes));
      });
    } else {
      durations.push(bookingDurationMinutes);
    }
  });

  return Math.min(...durations);
};

const tasksOverlapInTime = (taskA, taskB) =>
  taskA.unixStartTime < taskB.unixEndTime && taskB.unixStartTime < taskA.unixEndTime;

const getOverlapMinutes = (taskA, taskB) => {
  const overlapStart = Math.max(taskA.unixStartTime, taskB.unixStartTime);
  const overlapEnd = Math.min(taskA.unixEndTime, taskB.unixEndTime);

  if (overlapEnd <= overlapStart) return 0;
  return (overlapEnd - overlapStart) / 60;
};

const shouldGroupTasksForSplit = (taskA, taskB, servicesCatalog = []) => {
  if (!tasksOverlapInTime(taskA, taskB)) return false;
  if (taskA.orario_start === taskB.orario_start) return true;
  if (taskA.unixEndTime === taskB.unixEndTime) return true;

  const overlapMinutes = getOverlapMinutes(taskA, taskB);
  const minServiceDurationMinutes = getMinServiceDurationInGroup([taskA, taskB], servicesCatalog);
  const shorterBookingMinutes = Math.min(taskA.unixSize / 60, taskB.unixSize / 60);

  if (overlapMinutes >= minServiceDurationMinutes) return true;

  return overlapMinutes >= shorterBookingMinutes * 0.5;
};

const MAX_VISIBLE_OVERLAP_COLUMNS = 2;

const assignOverlapColumns = (group) => {
  if (group.length < 2) return null;

  const sortedByDuration = [...group].sort((a, b) => b.unixSize - a.unixSize);
  const primaryTask = sortedByDuration[0];
  const remaining = group
    .filter((task) => task.idprenotazione !== primaryTask.idprenotazione)
    .sort((a, b) => {
      if (a.unixStartTime !== b.unixStartTime) return a.unixStartTime - b.unixStartTime;
      return b.unixSize - a.unixSize;
    });

  const columnTasks = [[primaryTask]];

  remaining.forEach((task) => {
    let colIndex = 1;

    while (colIndex < columnTasks.length) {
      const hasConflict = columnTasks[colIndex].some((columnTask) =>
        tasksOverlapInTime(columnTask, task),
      );
      if (!hasConflict) break;
      colIndex++;
    }

    if (!columnTasks[colIndex]) columnTasks[colIndex] = [];
    columnTasks[colIndex].push(task);
  });

  return columnTasks;
};

const buildOverlapGroups = (tasks, servicesCatalog = []) => {
  const appointmentTasks = tasks.filter((task) => !task.limitCard && task.idprenotazione);
  const groups = [];
  const visited = new Set();

  appointmentTasks.forEach((task) => {
    if (visited.has(task.idprenotazione)) return;

    const group = [];
    const queue = [task];

    while (queue.length) {
      const currentTask = queue.shift();
      if (visited.has(currentTask.idprenotazione)) continue;

      visited.add(currentTask.idprenotazione);
      group.push(currentTask);

      appointmentTasks.forEach((candidateTask) => {
        if (visited.has(candidateTask.idprenotazione)) return;
        if (
          group.some((groupTask) =>
            shouldGroupTasksForSplit(groupTask, candidateTask, servicesCatalog),
          )
        ) {
          queue.push(candidateTask);
        }
      });
    }

    if (group.length >= 2) {
      groups.push(group);
    }
  });

  return groups;
};

export const assignOverlapLayout = (tasks, servicesCatalog = []) => {
  const overlapMetaById = {};

  buildOverlapGroups(tasks, servicesCatalog).forEach((group) => {
    const columnTasks = assignOverlapColumns(group);
    if (!columnTasks) return;

    const sortedGroup = [...group].sort(
      (a, b) => a.unixStartTime - b.unixStartTime || a.idprenotazione - b.idprenotazione,
    );
    const overlapTotal = group.length;
    const visibleColumns = Math.min(columnTasks.length, MAX_VISIBLE_OVERLAP_COLUMNS);
    const hiddenTasks = [];

    columnTasks.forEach((tasksInColumn, colIndex) => {
      if (colIndex >= MAX_VISIBLE_OVERLAP_COLUMNS) {
        hiddenTasks.push(...tasksInColumn);
        tasksInColumn.forEach((task) => {
          overlapMetaById[task.idprenotazione] = { overlapHidden: true };
        });
        return;
      }

      tasksInColumn.forEach((task) => {
        overlapMetaById[task.idprenotazione] = {
          isSplitOverlap: true,
          overlapIndex: colIndex,
          overlapMaxColumns: visibleColumns,
          overlapTotal,
          overlapExtraCount: 0,
          showOverlapBadge: false,
          overlapGroupTasks: sortedGroup,
        };
      });
    });

    if (hiddenTasks.length > 0) {
      const badgeColumnIndex = Math.min(1, visibleColumns - 1);
      const badgeColumn = columnTasks[badgeColumnIndex] || [];
      const badgeTask = badgeColumn.reduce(
        (latest, task) =>
          !latest || task.unixEndTime > latest.unixEndTime ? task : latest,
        null,
      );

      if (badgeTask && overlapMetaById[badgeTask.idprenotazione]) {
        overlapMetaById[badgeTask.idprenotazione] = {
          ...overlapMetaById[badgeTask.idprenotazione],
          showOverlapBadge: true,
          overlapExtraCount: hiddenTasks.length,
          overlapHiddenTasks: hiddenTasks,
        };
      }
    }
  });

  return tasks.map((task) => {
    const overlapMeta = overlapMetaById[task.idprenotazione];
    if (!overlapMeta) return task;
    return { ...task, ...overlapMeta };
  });
};

export const formatCalendarData = ({
  appointments,
  activeWorkers,
  activeDays,
  startWorkTime,
  endWorkTime,
  calendarItemsCount,
  timeStep,
  stepSize,
  services = [],
}) => {
  const stackedTasks = ({ startWork, activeTasks, calendarItemsCount }) => {
    return Array(calendarItemsCount)
      .fill(0)
      .reduce((acc, _, index) => {
        const stringState = startWork + (timeStep.hours * 60 + timeStep.minutes) * 60 * index;
        const selectedTasks = activeTasks.filter(
          (task) => stringState >= task.unixStartTime && stringState < task.unixEndTime,
        );
        if (selectedTasks.length) {
          const alreadyAddedTasks = acc.filter((task) =>
            selectedTasks.some((item) => item.idprenotazione === task.idprenotazione),
          );
          const newTasksToAdd = selectedTasks.filter(
            (task) =>
              !alreadyAddedTasks.some((item) => item.idprenotazione === task.idprenotazione),
          );
          const filledPosition = alreadyAddedTasks.map((item) => item.left);
          const max = filledPosition.length ? Math.max(...filledPosition) : null;
          const emptyPositions =
            max && max + 1 > alreadyAddedTasks.length
              ? Array(max)
                  .fill(0)
                  .map((_, index) => index)
                  .filter((item) => !filledPosition.includes(item))
              : [];
          const newTasks = newTasksToAdd.map((task, taskNumber) => {
            return {
              ...task,
              left: Number.isInteger(emptyPositions[taskNumber])
                ? emptyPositions[taskNumber]
                : alreadyAddedTasks.length + taskNumber,
            };
          });
          return [...acc, ...newTasks];
        }
        return acc;
      }, []);
  };
  return activeDays.map((date) => {
    const dayInDataBase = Object.keys(appointments).find((day) => date === day);

    const startWork = dayjs(`${date}T${startWorkTime.hours}:${startWorkTime.minutes}:00`).unix();
    const endWork = dayjs(`${date}T${endWorkTime.hours}:${endWorkTime.minutes}:00`).unix();
    const workTime = endWork - startWork;

    const workersWithTimeLimit = activeWorkers.map((worker) => {
      const workerTimeLimit = getWorkerClosuresForDate(worker, date);
      const workerWithTasks = {
        id: worker.idpoltrona,
        workerName: worker.nome_referente,
        workerTasks: [],
        iconPoltrona: worker.iconPoltrona,
        icon: getIconUrl(worker.idpoltrona, imageEntities.worker),
      };
      if (workerTimeLimit?.length) {
        const workersLimits = workerTimeLimit
          .map((limit) => {
            const interval = parseClosureInterval(limit, date, startWork, endWork);
            if (!interval) return null;

            const { start: realStart, end: realEnd } = interval;
            const startTask =
              ((realStart - startWork) / 60 / (timeStep.hours * 60 + timeStep.minutes)) * stepSize;
            const endTask =
              ((realEnd - realStart) / 60 / (timeStep.hours * 60 + timeStep.minutes)) * stepSize;

            return {
              limitCard: true,
              key: `${date}${worker.idpoltrona}${realStart}${realEnd}`,
              orario_start: limit.dalle,
              orario_end: limit.alle,
              unixStartTime: realStart,
              unixEndTime: realEnd,
              unixSize: realEnd - realStart,
              top: startTask,
              size: endTask,
            };
          })
          .filter(Boolean);
        workerWithTasks.workerTasks = workersLimits;
      }

      if (dayInDataBase) {
        const activeTasks = appointments[dayInDataBase]
          .filter((tasks) => String(tasks.idpoltrona) === String(worker.idpoltrona))
          .map((filteredTask) => {
            const unixStartTime = dayjs(`${date}T${filteredTask.orario_start}`).unix();
            const unixEndTime = dayjs(
              `${date}T${
                filteredTask.orario_end !== '00:00:00' ? filteredTask.orario_end : '24:00:00'
              }`,
            ).unix();

            const startTask =
              ((unixStartTime - startWork) / 60 / (timeStep.hours * 60 + timeStep.minutes)) *
              stepSize;
            const endTask =
              ((unixEndTime - unixStartTime) / 60 / (timeStep.hours * 60 + timeStep.minutes)) *
              stepSize;

            return {
              ...filteredTask,
              key: `${date}${worker.idpoltrona}${filteredTask.idprenotazione}`,
              orario_start: filteredTask.orario_start,
              orario_end: filteredTask.orario_end,
              idprenotazione: filteredTask.idprenotazione,
              nome: filteredTask.nome,
              unixStartTime,
              unixEndTime,
              unixSize: unixEndTime - unixStartTime,
              servizi: (filteredTask.servizi || []).map((service) => ({
                color: service.color,
                nome: service.nome,
                durata: service.durata,
                idServizio: service.idServizio,
                idservezio: service.idservezio,
                icon: getIconUrl(service.idServizio, imageEntities.service),
                iconaServizio: service.iconaServizio,
              })),
              top: startTask,
              size: endTask,
              left: 0,
              color: filteredTask.servizi?.length ? filteredTask.servizi[0].color : 'gray',
              textColor: getReadableTextColor(
                filteredTask.servizi?.length ? filteredTask.servizi[0].color : 'gray',
              ),
              isBreak: isCalendarBreak(filteredTask) ? 1 : filteredTask.isBreak,
              isActive: unixStartTime > dayjs().unix(),
            };
          });

        workerWithTasks.workerTasks = assignOverlapLayout(
          [...workerWithTasks.workerTasks, ...activeTasks],
          services,
        );

        // const formedTasks = stackedTasks({
        //   startWork,
        //   activeTasks,
        //   calendarItemsCount,
        // });
        //
        // workerWithTasks.workerTasks = [...workerWithTasks.workerTasks, ...formedTasks];
      }
      return {
        ...workerWithTasks,
        fullyClosed: isWorkerFullyClosed(workerWithTasks, startWork, endWork),
      };
    });

    const closedWorkersCount = workersWithTimeLimit.filter((worker) => worker.fullyClosed).length;
    const fullyClosed =
      workersWithTimeLimit.length > 0 && closedWorkersCount === workersWithTimeLimit.length;

    return {
      date: date,
      startWork,
      endWork,
      workTime,
      workers: workersWithTimeLimit,
      fullyClosed,
      closedWorkersCount,
    };
  });
};
